import { Icon } from "@mdotm/shadcn/legacy/components";
import { toClassName, useDrivenState } from "@mdotm/shadcn/legacy/react-extensions";
import type { GridApi } from "ag-grid-community";
import type { CustomHeaderProps } from "ag-grid-react";
import { useCallback, useEffect, useMemo } from "react";
import { CompositionViewMode, type MergedCompositionRow } from "../rows";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";

type LabAutoGroupHeaderProps = CustomHeaderProps<MergedCompositionRow> & {
	viewMode: CompositionViewMode;
	/** Identity of the current group-by; changes whenever the tree is rebuilt. */
	groupKey: string;
};

/** True when at least one group row exists and every group row is currently expanded. */
function areAllGroupsExpanded(api: GridApi<MergedCompositionRow>): boolean {
	let hasGroup = false;
	let allExpanded = true;
	api.forEachNode((node) => {
		if (node.group) {
			hasGroup = true;
			if (!node.expanded) {
				allExpanded = false;
			}
		}
	});
	return hasGroup && allExpanded;
}

/**
 * Header for the tree auto-group column: an expand/collapse-all toggle followed by the group title.
 * The `Double-right` icon points right when collapsed and rotates 90° when everything is expanded.
 */
export function LabAutoGroupHeader(params: LabAutoGroupHeaderProps): React.JSX.Element {
	const { api, viewMode, groupKey } = params;

	// Ungroup is a flat list with no group rows, so expand/collapse-all is a no-op there.
	const disabled = viewMode === CompositionViewMode.ungroup;

	// Each group-by change rebuilds the tree and re-applies the grid's default expansion
	// (groups expanded), so drive the toggle off the group identity: a new group-by resets
	// the icon to expanded, while clicks and individual group toggles override it locally.
	const driver = useMemo(() => ({ key: groupKey, expanded: true }), [groupKey]);
	const [{ expanded }, setDriven] = useDrivenState(driver);
	const setExpanded = useCallback(
		(value: boolean) => {
			setDriven((cur) => ({ ...cur, expanded: value }));
		},
		[setDriven],
	);

	// Keep the icon honest when the user expands/collapses individual groups elsewhere.
	useEffect(() => {
		const sync = () => {
			setExpanded(areAllGroupsExpanded(api));
		};
		api.addEventListener("rowGroupOpened", sync);
		api.addEventListener("expandOrCollapseAll", sync);
		return () => {
			api.removeEventListener("rowGroupOpened", sync);
			api.removeEventListener("expandOrCollapseAll", sync);
		};
	}, [api, setExpanded]);

	const toggle = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			if (expanded) {
				api.collapseAll();
			} else {
				api.expandAll();
			}
		},
		[api, expanded],
	);

	return (
		<div className="flex items-center gap-1 w-full h-full">
			<button
				type="button"
				onClick={toggle}
				disabled={disabled}
				aria-label={expanded ? "Collapse all" : "Expand all"}
				data-qualifier="PortfolioLab/CompositionEditor/ExpandCollapseAll"
				className="lab-autogroup-expander flex items-center justify-center shrink-0 cursor-pointer text-(--palette-N500) disabled:cursor-default disabled:opacity-40"
			>
				<Icon
					icon="Double-right"
					size={16}
					color={themeCSSVars.palette_N700}
					className={toClassName({
						"transition-transform duration-150": true,
						"rotate-90": expanded,
						"rotate-0": !expanded,
					})}
				/>
			</button>
			<span className="ag-header-cell-text truncate">{params.displayName}</span>
		</div>
	);
}
